import {
  decodePage,
  isRecord,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireNumber,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']

function decodeGroupSource(raw: unknown): Schemas['GroupSource'] {
  if (raw === 'manual' || raw === 'scim') return raw
  throw new Error('Malformed group source')
}

export type EntityGroup = Schemas['GroupDto']

export const groupSourceLabels = {
  manual: 'Manual',
  scim: 'SCIM',
} satisfies Record<EntityGroup['source'], string>

export type InheritedEntityGroup = Schemas['InheritedGroupDto']

export type EntityGroupsList = Schemas['GroupsResponse']

export type CreateEntityGroupInput = Omit<
  Schemas['CreateGroupRequest'],
  'name' | 'description'
> & { name: string; description: string }

export type CreatedEntityGroup = Schemas['CreateGroupResponse']

export type GroupMember = Schemas['GroupMemberDto']

export type GrantedRole = Schemas['GrantedRoleDto']

export type GroupGrant = Schemas['GroupAccessGrantDto']

export type GroupDetail = Schemas['GroupDetailResponse']

function decodeCreatedEntityGroup(raw: unknown): CreatedEntityGroup {
  if (!isRecord(raw)) throw new Error('Malformed created group')
  return {
    groupId: requireString(raw.groupId, 'groupId'),
    ownerEntityId: requireString(raw.ownerEntityId, 'ownerEntityId'),
    name: requireString(raw.name, 'name'),
    description: requireString(raw.description, 'description'),
  } satisfies CreatedEntityGroup
}

function decodeEntityGroupRow(raw: unknown): EntityGroup {
  if (!isRecord(raw) || !Array.isArray(raw.roles))
    throw new Error('Malformed entity group')
  return {
    groupId: requireString(raw.groupId, 'groupId'),
    name: requireString(raw.name, 'name'),
    source: decodeGroupSource(raw.source),
    active: requireBoolean(raw.active, 'active'),
    managedByScim: requireBoolean(raw.managedByScim, 'managedByScim'),
    memberCount: requireNumber(raw.memberCount, 'memberCount'),
    roles: raw.roles.map(decodeGrantedRole),
  } satisfies EntityGroup
}

function decodeInheritedEntityGroup(raw: unknown): InheritedEntityGroup {
  if (!isRecord(raw) || !Array.isArray(raw.roles))
    throw new Error('Malformed inherited group')
  return {
    groupId: requireString(raw.groupId, 'groupId'),
    name: requireString(raw.name, 'name'),
    source: decodeGroupSource(raw.source),
    active: requireBoolean(raw.active, 'active'),
    memberCount: requireNumber(raw.memberCount, 'memberCount'),
    roles: raw.roles.map(decodeGrantedRole),
    sourceEntityId: requireString(raw.sourceEntityId, 'sourceEntityId'),
    sourceEntityName: requireString(raw.sourceEntityName, 'sourceEntityName'),
  } satisfies InheritedEntityGroup
}

function decodeEntityGroups(raw: unknown): EntityGroupsList {
  if (!isRecord(raw) || !Array.isArray(raw.items) || !Array.isArray(raw.inherited))
    throw new Error('Malformed entity groups response')
  return {
    items: raw.items.map(decodeEntityGroupRow),
    inherited: raw.inherited.map(decodeInheritedEntityGroup),
  } satisfies EntityGroupsList
}

function decodeGroupMember(raw: unknown): GroupMember {
  if (!isRecord(raw) || !Array.isArray(raw.authorities))
    throw new Error('Malformed group member')
  return {
    userId: requireString(raw.userId, 'userId'),
    name: requireString(raw.name, 'name'),
    email: nullableString(raw.email, 'email'),
    authorities: raw.authorities.map(decodeGroupMemberAuthority),
  } satisfies GroupMember
}

export function decodeGroupMemberAuthoritiesJson(value: string): GroupMember['authorities'] {
  const raw: unknown = JSON.parse(value)
  if (!Array.isArray(raw)) throw new Error('Malformed group member authorities')
  return raw.map(decodeGroupMemberAuthority)
}

function decodeGroupMemberAuthority(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed group member authority')
  return {
    authority: requireString(raw.authority, 'authority'),
    displayName: requireString(raw.displayName, 'displayName'),
  }
}

const decodeMemberCandidatesPage = (raw: unknown) =>
  decodePage(raw, decodeGroupMember, 'member candidates page')

function decodeGrantedRole(raw: unknown): GrantedRole {
  if (!isRecord(raw)) throw new Error('Malformed granted role')
  return {
    roleId: requireString(raw.roleId, 'roleId'),
    name: requireString(raw.name, 'name'),
  } satisfies GrantedRole
}

export function decodeGroupsGridRolesJson(value: string): GrantedRole[] {
  const raw: unknown = JSON.parse(value)
  if (!Array.isArray(raw)) throw new Error('Malformed group roles')
  return raw.map(decodeGrantedRole)
}

function decodeGroupGrant(raw: unknown): GroupGrant {
  if (!isRecord(raw) || !Array.isArray(raw.roles))
    throw new Error('Malformed group grant')
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    entityName: requireString(raw.entityName, 'entityName'),
    roles: raw.roles.map(decodeGrantedRole),
  } satisfies GroupGrant
}

function decodeGroupDetail(raw: unknown): GroupDetail {
  if (!isRecord(raw) || !isRecord(raw.members) || !Array.isArray(raw.grants))
    throw new Error('Malformed group detail response')
  return {
    groupId: requireString(raw.groupId, 'groupId'),
    ownerEntityId: requireString(raw.ownerEntityId, 'ownerEntityId'),
    name: requireString(raw.name, 'name'),
    description: requireString(raw.description, 'description'),
    canManage: requireBoolean(raw.canManage, 'canManage'),
    source: decodeGroupSource(raw.source),
    active: requireBoolean(raw.active, 'active'),
    managedByScim: requireBoolean(raw.managedByScim, 'managedByScim'),
    memberCount: requireNumber(raw.memberCount, 'memberCount'),
    members: decodePage(raw.members, decodeGroupMember, 'group members page'),
    grants: raw.grants.map(decodeGroupGrant),
  } satisfies GroupDetail
}

export const groupsApi = {
  getEntityGroups: (entityId: string) => request(`/api/v1/entities/${entityId}/groups`, decodeEntityGroups),
  createEntityGroup: (entityId: string, input: CreateEntityGroupInput) =>
    request(`/api/v1/entities/${entityId}/groups`, decodeCreatedEntityGroup, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  getGroupDetail: (entityId: string, groupId: string) =>
    request(`/api/v1/entities/${entityId}/groups/${groupId}`, decodeGroupDetail),
  getGroupMemberCandidates: (entityId: string, groupId: string, options?: { after?: string; query?: string }) => {
    const query = new URLSearchParams()
    if (options?.after) query.set('after', options.after)
    if (options?.query) query.set('q', options.query)
    const qs = query.toString()
    return request(
      `/api/v1/entities/${entityId}/groups/${groupId}/member-candidates${qs ? `?${qs}` : ''}`,
      decodeMemberCandidatesPage,
    )
  },
  addGroupMember: (entityId: string, groupId: string, userId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/groups/${groupId}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId } satisfies Schemas['AddMemberRequest']),
    }),
  removeGroupMember: (entityId: string, groupId: string, userId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),
  assignGroupRole: (entityId: string, groupId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/groups/${groupId}/roles`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roleId } satisfies Schemas['AssignGroupRoleRequest']),
    }),
  revokeGroupRole: (entityId: string, groupId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/groups/${groupId}/roles/${roleId}`, { method: 'DELETE' }),
}
